import { useState, useEffect } from "react";
import { C } from "../../constants";
import { Ic } from "../../components/ui/Icons";
import { HoverCard, SubCard, GridCard, Badge } from "../../components/ui/Components";
import { apiCall } from "../../api";
import SubmissionDetail from "./SubmissionDetail";
import EstimatorTool from "./EstimatorTool";
import ProductSelection from "../ProductSelection";
import CleanAgentForm from "./forms/CleanAgentForm";
import KitchenForm from "./forms/KitchenForm";
import InertForm from "./forms/InertForm";
import AerosolForm from "./forms/AerosolForm";
import WaterMistForm from "./forms/WaterMistForm";
import OtherSuppressionForm from "./forms/OtherSuppressionForm";
import EarlyWarningForm from "./forms/EarlyWarningForm";
import AddressableForm from "./forms/AddressableForm";
import OtherDetectionForm from "./forms/OtherDetectionForm";

const SuppressionTypes = [
  { label: "Clean Agent", color: "#3B82F6", emoji: "🖥️", desc: "Suppression for Critical & Sensitive Infrastructure", formKey: "clean-agent" },
  { label: "Inert", color: "#6366F1", emoji: "💨", desc: "CO2 or Nitrogen/Argon Suppression Systems", formKey: "inert" },
  { label: "Aerosol", color: "#8B5CF6", emoji: "🧯", desc: "Condensed Aerosol Suppression", formKey: "aerosol" },
  { label: "Kitchen Suppression", color: "#EF4444", emoji: "🍳", desc: "Kitchen Protection Systems", formKey: "kitchen" },
  { label: "Water Mist", color: "#0EA5E9", emoji: "💧", desc: "Water Mist Suppression", formKey: "water-mist" },
  { label: "Other", color: "#64748B", emoji: "📋", desc: "Custom requirements", formKey: "other-suppression" },
];

const DetectionTypes = [
  { label: "Early Warning Aspirating Detection", color: "#F59E0B", emoji: "🔬", desc: "Aspirating detection systems", formKey: "early-warning" },
  { label: "Addressable Detection Solutions", color: "#10B981", emoji: "🔔", desc: "Addressable panels & devices", formKey: "addressable" },
  { label: "Other", color: "#64748B", emoji: "📋", desc: "Custom requirements", formKey: "other-detection" },
];

function BackButton({ onClick }) {
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit", padding: "4px 0", marginBottom: 20, transition: "color 0.2s" }}
      onMouseEnter={e => e.currentTarget.style.color = C.navy} onMouseLeave={e => e.currentTarget.style.color = C.t3}>
      <Ic.ChevronLeft /> Back
    </button>
  );
}

function SubmissionsTracker({ submissions, loading, onSelect }) {
  const [filterBy, setFilterBy] = useState("");
  const [filterField, setFilterField] = useState("all");

  const filtered = submissions.filter(s => {
    if (!filterBy.trim()) return true;
    const term = filterBy.toLowerCase();
    if (filterField === "submittedBy") return (s.submittedBy || "").toLowerCase().includes(term);
    if (filterField === "company") return (s.company || "").toLowerCase().includes(term);
    return (s.submittedBy || "").toLowerCase().includes(term) || (s.company || "").toLowerCase().includes(term);
  });

  return (
    <div style={{ background: C.white, borderRadius: 14, border: `1px solid ${C.border}`, overflow: "hidden", marginTop: 32 }}>
      <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: C.t1, margin: 0 }}>Project Requests</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={filterField} onChange={e => setFilterField(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, background: C.grayBg, color: C.t2, outline: "none", fontFamily: "inherit", cursor: "pointer" }}>
            <option value="all">All Fields</option>
            <option value="submittedBy">Submitted By</option>
            <option value="company">Company</option>
          </select>
          <input type="text" placeholder="Filter..." value={filterBy} onChange={e => setFilterBy(e.target.value)}
            style={{ padding: "6px 12px", borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 12, outline: "none", background: C.grayBg, fontFamily: "inherit", width: 160 }} />
          {filterBy && (
            <button onClick={() => setFilterBy("")}
              style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 18, lineHeight: 1, padding: "0 2px" }}>×</button>
          )}
          <span style={{ fontSize: 12, color: C.t3 }}>{filtered.length} of {submissions.length}</span>
        </div>
      </div>
      <div style={{ padding: "8px 24px", display: "grid", gridTemplateColumns: "110px 1fr 140px 160px 90px 80px 30px", alignItems: "center", gap: 12, borderBottom: `1px solid ${C.border}`, background: C.grayBg }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Request ID</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Solution Type</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Submitted By</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Company</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Date</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</span>
        <span></span>
      </div>
      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center", color: C.t3, fontSize: 14 }}>
            {submissions.length === 0 ? "No submissions yet. Submit a project request to get started." : "No submissions match your filter."}
          </div>
        ) : (
          filtered.map((s, i) => (
            <div key={s.id} onClick={() => onSelect(s)}
              style={{ padding: "12px 24px", display: "grid", gridTemplateColumns: "110px 1fr 140px 160px 90px 80px 30px", alignItems: "center", gap: 12, borderBottom: i < filtered.length - 1 ? `1px solid ${C.border}` : "none", transition: "background 0.15s", cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = C.grayBg}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.info }}>{s.id}</span>
              <span style={{ fontSize: 13, color: C.t1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.type}</span>
              <span style={{ fontSize: 12, color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.submittedBy}</span>
              <span style={{ fontSize: 12, color: C.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.company || "—"}</span>
              <span style={{ fontSize: 12, color: C.t3 }}>{s.date ? s.date.toString().slice(0, 10) : ""}</span>
              <Badge status={s.status} />
              {s.hasDocs && <span style={{ color: C.t3, display: "flex" }}><Ic.Doc /></span>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function ProjectRequest({ user, onUnsavedChange }) {
  const [step, setStep] = useState("main");
  const [activeForm, setActiveForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const isAdmin = (user.role || "").toUpperCase() === "ADMIN";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSubmissions(); }, []);

  const loadSubmissions = async () => {
    setLoadingSubmissions(true);
    try {
      const r = await apiCall("getRequests", { email: user.email, company: user.company, role: user.role || "CLIENT" });
      if (r.success && r.requests) {
        setSubmissions(r.requests.map(s => ({ ...s, submittedBy: s.contactName })));
      }
    } catch (err) { console.error("Failed to load submissions:", err); }
    setLoadingSubmissions(false);
  };

  const goMain = () => {
    setStep("main"); setActiveForm(null);
    if (onUnsavedChange) onUnsavedChange(false);
    loadSubmissions();
  };

  const openForm = (formKey, solutionType) => {
    setActiveForm({ formKey, solutionType });
    setStep("form");
    if (onUnsavedChange) onUnsavedChange(true);
  };

  const handleSubmit = async (formData, files) => {
    try {
      const r = await apiCall("submitRequest", {
        email: user.email, company: user.company, contactName: user.name,
        country: user.country, solutionType: formData.solutionType, formData,
        files: (files || []).map(f => ({ name: f.name, mimeType: f.mimeType, base64: f.base64 })),
      });
      if (r.success) {
        setSubmissions(prev => [{ id: r.requestId, type: formData.solutionType, date: r.dateSubmitted, status: "pending", hasDocs: (files || []).length > 0, submittedBy: user.name, company: user.company }, ...prev]);
      }
    } catch (err) { console.error("Failed to submit request:", err); }
    if (onUnsavedChange) onUnsavedChange(false);
  };

  const back = () => {
    if (step === "form") { setStep("suppression"); setActiveForm(null); if (onUnsavedChange) onUnsavedChange(false); }
    else if (step === "suppression" || step === "detection") setStep("eng-sub");
    else if (step === "eng-sub" || step === "estimator-warning" || step === "products") setStep("main");
    else setStep("main");
  };

  if (step === "form" && activeForm) {
    switch (activeForm.formKey) {
      case "clean-agent":       return <CleanAgentForm user={user} onSubmit={handleSubmit} onBack={back} onComplete={goMain} />;
      case "kitchen":           return <KitchenForm user={user} onSubmit={handleSubmit} onBack={back} onComplete={goMain} />;
      case "inert":             return <InertForm user={user} onSubmit={handleSubmit} onBack={back} onComplete={goMain} />;
      case "aerosol":           return <AerosolForm user={user} onSubmit={handleSubmit} onBack={back} onComplete={goMain} />;
      case "water-mist":        return <WaterMistForm user={user} onSubmit={handleSubmit} onBack={back} onComplete={goMain} />;
      case "other-suppression": return <OtherSuppressionForm user={user} onSubmit={handleSubmit} onBack={back} onComplete={goMain} />;
      case "early-warning":     return <EarlyWarningForm user={user} onSubmit={handleSubmit} onBack={back} onComplete={goMain} />;
      case "addressable":       return <AddressableForm user={user} onSubmit={handleSubmit} onBack={back} onComplete={goMain} />;
      case "other-detection":   return <OtherDetectionForm user={user} onSubmit={handleSubmit} onBack={back} onComplete={goMain} />;
      default:
        return (
          <div style={{ maxWidth: 600 }}>
            <BackButton onClick={back} />
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🚧</div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>{activeForm.solutionType}</h2>
              <p style={{ fontSize: 14, color: C.t2 }}>This form will be available in the next update.</p>
            </div>
          </div>
        );
    }
  }

  if (step === "estimator-warning") return <EstimatorTool user={user} onBack={back} onUnsavedChange={onUnsavedChange} />;

  if (step === "products") {
    return (
      <ProductSelection
        userInfo={user}
        onAbandon={goMain}
        onUnsavedChange={onUnsavedChange}
        onSubmitSuccess={() => { if (onUnsavedChange) onUnsavedChange(false); loadSubmissions(); }}
        onSubmitComplete={goMain}
      />
    );
  }

  const detailModal = selectedSubmission ? (
    <SubmissionDetail
      submission={selectedSubmission}
      onClose={() => setSelectedSubmission(null)}
      onUpdate={() => loadSubmissions()}
      isAdmin={isAdmin}
    />
  ) : null;

  if (step === "main") {
    return (
      <div style={{ maxWidth: 1200 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.t1, margin: "0 0 6px" }}>What would you like to do?</h1>
          <p style={{ fontSize: 14, color: C.t2, margin: 0 }}>Select the type of request that best fits your needs.</p>
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <HoverCard onClick={() => setStep("eng-sub")} color="#3B82F6" emoji="🏗️" title="Engineering & Design Request" subtitle="Submit detailed project requirements for engineering review, system calculation, and finalized design." arrow />
          <HoverCard onClick={() => setStep("estimator-warning")} color="#F59E0B" emoji="📐" title="System Estimation Tool" subtitle="Get a general pre-calculation estimate based on your design specs. For budgeting and early-stage planning." arrow />
          <HoverCard onClick={() => setStep("products")} color="#10B981" emoji="📦" title="Product Selection" subtitle="Already know what you need? Submit a list of part numbers and quantities for a direct quote." arrow />
        </div>
        <SubmissionsTracker submissions={submissions} loading={loadingSubmissions} onSelect={s => setSelectedSubmission(s)} />
        {detailModal}
      </div>
    );
  }

  if (step === "eng-sub") {
    return (
      <div style={{ maxWidth: 560 }}>
        <BackButton onClick={back} />
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: "0 0 6px" }}>Engineering & Design Request</h2>
        <p style={{ fontSize: 14, color: C.t2, margin: "0 0 24px" }}>What type of system are you looking to design?</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SubCard onClick={() => setStep("suppression")} color="#EF4444" emoji="💧" label="Suppression Systems" desc="Fire suppression design & engineering" />
          <SubCard onClick={() => setStep("detection")} color="#F59E0B" emoji="🚨" label="Detection Systems" desc="Fire detection design & engineering" />
        </div>
      </div>
    );
  }

  if (step === "suppression") {
    return (
      <div style={{ maxWidth: 700 }}>
        <BackButton onClick={back} />
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: "0 0 6px" }}>Suppression Systems</h2>
        <p style={{ fontSize: 14, color: C.t2, margin: "0 0 24px" }}>Select the type of suppression system.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14 }}>
          {SuppressionTypes.map(t => <GridCard key={t.label} onClick={() => openForm(t.formKey, `Suppression: ${t.label}`)} color={t.color} emoji={t.emoji} label={t.label} desc={t.desc} />)}
        </div>
      </div>
    );
  }

  if (step === "detection") {
    return (
      <div style={{ maxWidth: 700 }}>
        <BackButton onClick={back} />
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: "0 0 6px" }}>Detection Systems</h2>
        <p style={{ fontSize: 14, color: C.t2, margin: "0 0 24px" }}>Select the type of detection system.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14 }}>
          {DetectionTypes.map(t => <GridCard key={t.label} onClick={() => openForm(t.formKey, `Detection: ${t.label}`)} color={t.color} emoji={t.emoji} label={t.label} desc={t.desc} />)}
        </div>
      </div>
    );
  }

  return null;
}
